/**
 * Profile-completeness gate — single source of truth.
 *
 * The same fields that block event entry (see RegistrationModal's
 * `isProfileComplete`) also block invite acceptance. We mirror that field
 * list here so a future change only has to be made in one place. Keep
 * this list in sync with RegistrationModal's `REQUIRED_PROFILE_FIELDS`
 * (and the backend's seed/registration validation, when it grows one).
 */

export const REQUIRED_PROFILE_FIELDS = [
  'first_name',
  'last_name',
  'phone',
  'address1',
  'city',
  'state',
  'zip',
  'date_of_birth',
  'gender',
] as const;

export type RequiredProfileField = (typeof REQUIRED_PROFILE_FIELDS)[number];

/**
 * Returns true when every field in REQUIRED_PROFILE_FIELDS is present
 * and non-empty on the supplied profile. Returns false for null /
 * undefined profiles so unauthenticated callers get a safe default.
 */
export function isFullProfileComplete(
  profile: Record<string, unknown> | null | undefined,
): boolean {
  if (!profile) return false;
  for (const field of REQUIRED_PROFILE_FIELDS) {
    const val = profile[field];
    if (val === undefined || val === null || val === '') return false;
    if (typeof val === 'string' && val.trim() === '') return false;
  }
  return true;
}
